<script setup lang="ts">
	useHead({
		meta: [
			{ name: 'geo.placename', content: 'Kurt-Eisner-Straße, 04275 Leipzig, Germany'},
			{ name: 'geo.position', content: '51.320710,12.374496'},
			{ name: 'geo.region', content: 'DE-SN'},
			{ name: 'theme-color', content: '#1E3B95'},
			{ name: 'msapplication-TileColor', content: '#1E3B95'},
			{ property: 'og:type', content: 'website'},
			{ property: 'og:locale', content: 'de_DE'}
		],
		link: [
			{ rel: 'icon', type: 'image/png', sizes: '16x16', content: usePublicUrl('/icons/favicon-16x16.png')},
			{ rel: 'icon', type: 'image/png', sizes: '32x32', content: usePublicUrl('/icons/favicon-32x32.png')},
			{ rel: 'icon', type: 'image/png', sizes: '192x192', content: usePublicUrl('/icons/android-chrome-192x192.png')},
			{ rel: 'icon', type: 'image/png', sizes: '512x512', content: usePublicUrl('/icons/android-chrome-512x512.png')},
			{ rel: 'apple-touch-icon', content: usePublicUrl('/icons/apple-touch-icon.png')},
			{ rel: 'msapplication-TileImage', content: usePublicUrl('/icons/tileImage-144x144.png')}
		]
	})

	const { data: navigationItems } = await useAsyncData('navigationItems', () => $fetch('/data/mainNavigation.json?v=260414'))
	const { data: overlayNavigationItems } = await useAsyncData('overlayNavigationItems', () => $fetch('/data/overlayNavigation.json?v=260414'))
	const { data: mobileNavigationItems } = await useAsyncData('mobileNavigationItems', () => $fetch('/data/mobileNavigation.json?v=260415'))
	const { data: footerLinks } = await useAsyncData('footerLinks', () => $fetch('/data/footerNavigation.json?v=260414'))

	//-- load profile
	const rConf = useRuntimeConfig()

	const profile = ref({})
	const cachedData = useNuxtData('profile')
	if (cachedData.data.value) {
		profile.value = cachedData.data.value
	} else {
		const { data } = await useFetch('/rest/profiles', {
			baseURL: rConf.public.apiBase,
			key: 'profile'
		})
		if(data.value && data.value.info.count === 1) {
			profile.value = data.value.items[0]
		}
	}

	const header = ref(null)
	const isScrolled = ref<boolean>(false)
	const isDropdownOpen = ref(false)

	const route = useRoute()
	const drawerOpen = ref(false)

	onMounted(() => {
		useEventListener(window, 'scroll', function(){
			isScrolled.value = window.scrollY > header.value.clientHeight
		})
	})

	watch(route, value => {
		if(drawerOpen.value) {
			drawerOpen.value = false
		}
  	}, {deep: true, immediate: true})

	provide('headerDropdownOpen', isDropdownOpen)
</script>

<template>
	<GsApp color-mode-preference="light">
		<NuxtLayout name="site-default">
			<template #header>
				<div ref="header" class="fluid transition duration-300 ease-in-out bg-white/90" :class="{'bg-white drop-shadow-xl z-100':isScrolled, 'bg-white!': isDropdownOpen}">
					<GsPageSection as="div" container-class="" class="relative px-0 lg:px-24 xl:px-32 [@media(min-width:2100px)]:px-40">

						<div class="h-full w-px bg-red-300 absolute z-0 left-1/2 -translate-x-1/2 hidden" aria-hidden="true"></div>
						<div class="w-full h-px bg-red-300 absolute z-0 top-1/2 -translate-y-1/2 hidden" aria-hidden="true"></div>

						<div class="flex justify-between">
							<div class="w-45 lg:w-70 xl:w-75 2xl:w-80 [@media(min-width:2100px)]:w-95 my-auto pe-2 sm:pe-0 py-2 lg:py-3" :class="{'md:w-45! lg:w-55! sm:py-1! lg:py-2!': isScrolled}">
								<NuxtLink :to="{name: 'home'}" class="block" title="zur Startseite">
									<img v-lazy-load  data-src="@/assets/img/D&P-Logo.png" alt="Logo der D & P Planungsgesellschaft mbH" class="w-full" width="920" height="263" />
								</NuxtLink>
							</div>
							<div class="hidden lg:flex lg:justify-center w-full">
								<Navigation :items="navigationItems" :isScrolled="isScrolled" :class="isScrolled ? '-translate-x-7.25': '-translate-x-6'"></Navigation>
							</div>
							<div v-if="$screen.gte.lg" class="flex justify-end w-45 lg:w-75 xl:w-85 [@media(min-width:2100px)]:w-99.5" :class="{'md:w-45! lg:w-55!': isScrolled}">
								<NavigationOverlay :items="overlayNavigationItems"></NavigationOverlay>
							</div>
							<div v-if="$screen.lt.lg" class="flex">
								<UDrawer
									direction="left"
									:ui="{
										content: 'w-full xs:w-auto max-w-none'
								  	}"
									v-model:open="drawerOpen"
								>
									 <LayoutBtnToggle class="my-auto"></LayoutBtnToggle>
									 <template #header>
										<h2 class="sr-only">Navigation</h2>
									</template>
									<template #body>
										<div class="w-full xs:w-80 sm:w-95 h-full relative">
											<NuxtLink :to="{name: 'home'}" class="block" title="zur Startseite">
												<img v-lazy-load  data-src="@/assets/img/D&P-Logo.png" alt="Logo der D & P Planungsgesellschaft mbH" class="max-w-1/2 pb-4" width="920" height="263" />
											</NuxtLink>
											<hr class="border-gray-200"/>
											<NavigationMobile :items="mobileNavigationItems"></NavigationMobile>
										</div>
									</template>
								</UDrawer>
							</div>
						</div>
					</GsPageSection>
				</div>
			</template>

			<div class="relative">
				<NuxtPage/>
			</div>

			<template #footer>
				<GsPageSection class="pb-10 pt-2.5 lg:py-3.5 border-t border-gray-800 bg-gray-100 lg:bg-white lg:mx-16 **:text-sm" container-class="md:px-0!">
					<div class="flex flex-col lg:flex-row justify-between gap-6 lg:gap-10">
						<div class="text-center lg:text-left order-1 lg:order-0">
							<p>© D&P Planungsgesellschaft mbH - Planungen im Neubau und Altbau (1992-{{ new Date().getFullYear() }}) - Alle Rechte vorbehalten.</p>
						</div>
						<div v-if="footerLinks && footerLinks.length" class="text-center lg:text-left order-0 lg:order-1">
							<nav role="navigation" class="flex flex-col md:flex-row justify-center lg:justify-start md:space-x-6 lg:space-x-8 space-y-4 md:space-y-0">
								<NuxtLink v-for="(item,idx) in footerLinks" :to="{name: item.name, params: item.slug ? { article_slug: item.slug } : {}}" no-prefetch class="hover:text-primary transition duration-300 ease-in-out" :title="item.label">{{ item.label }}</NuxtLink>
							</nav>
						</div>
					</div>
				</GsPageSection>
			</template>
		</NuxtLayout>
	</GsApp>
</template>

<style>
	header {
		position: fixed;
		left: 0;
		top:0;
		width: 100%;
		z-index: 40;
	}
	header .router-link-active,
	nav .router-link-active {
		color: var(--color-primary);
	}
	.page-enter-active,
	.page-leave-active {
		transition: all 0.1s;
	}
	.page-enter-from,
	.page-leave-to {
		opacity: 0;
		filter: blur(.5rem);
	}
</style>
